<template>
  <div class="mb-2">Share a direct link to this copyright</div>
  <div class="relative">
    <Input
      :model-value="url"
      size="xl"
      readonly
      class="pointer-events-none truncate pr-[110px]"
    />
    <button
      type="button"
      class="absolute top-1/2 right-[16px] flex -translate-y-1/2 cursor-pointer items-center whitespace-nowrap font-medium text-green-700"
      @click="handleCopyClick"
    >
      <template v-if="copied">Copied!</template>
      <template v-else>
        <LinkIcon class="mr-1 size-4 text-green-700" />
        Copy link
      </template>
    </button>
  </div>

  <template v-if="showInvite">
    <div class="mt-[26px] mb-5 h-px w-full bg-gray-800/20" />

    <div class="mb-1 flex items-center font-medium text-[14px] leading-[18px]">
      <SendIcon class="mr-2.5 size-[18px] text-gray-800" />
      <div>Invite to view and download this file</div>
    </div>
    <div>Control who can view this file and who has download access</div>

    <Link
      v-if="file.viewers?.totalCount"
      icon="arrow"
      class="mt-1"
      @click="handleAccessClick"
    >
      {{ viewerCount }}
    </Link>
    <Button
      variant="stroke"
      :size="isMobile ? 'lg' : 'md'"
      class="mt-3"
      @click="handleInviteClick"
    >
      Invite user
    </Button>
  </template>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { Button, Input, Link } from '@dacr/library'
import { LinkIcon, SendIcon } from '@dacr/icons'
import { FileRole } from '@/shared/graphql/router/graphql'
import type { File } from '@/shared/graphql/router/graphql'
import { openDialog, Dialog } from '@/shared/dialogs/dialog'
import { useDevice } from '@/shared/composables/useDevice'

const { isMobile } = useDevice()

const props = defineProps<{
  file: File
}>()

const file = ref({ ...props.file })
const copied = ref(false)
let interval: ReturnType<typeof setTimeout>

const url = computed(
  () => `${window.location.origin}/file/${file.value.copyright.id}`
)

const showInvite = computed(
  () =>
    file.value.role === FileRole.Owner || file.value.role === FileRole.CoOwner
)

const viewerCount = computed(() => {
  const count = file.value.viewers?.totalCount
  if (count === 1) return '1 person'
  return `${count} people`
})

const handleCopyClick = async () => {
  clearInterval(interval)
  await navigator.clipboard.writeText(url.value)
  copied.value = true

  interval = setTimeout(() => {
    copied.value = false
  }, 1500)
}

const openAccess = () => {
  openDialog(Dialog.FileAccess, {
    props: { file: file.value },
    stack: true,
    events: {
      success(data) {
        file.value = data as File
      },
    },
  })
}

const handleAccessClick = () => openAccess()

const handleInviteClick = () => openAccess()
</script>
